import { Bell, CreditCard, HelpCircle, LogOut, Menu as MenuIcon, Moon, Search, Sun, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { CommandPalette } from "@/components/CommandPalette";
import { AiConsentDialog } from "@/components/AiConsentDialog";
import { UpgradeDialog } from "@/components/plans/UpgradeDialog";
import { ReminderAlarms } from "@/components/reminders/ReminderAlarms";
import { PLAN_NAMES } from "@/lib/types";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { Kbd } from "@/components/ui/misc";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "@/components/ui/menu";
import { useMe, useUpdateProfile } from "@/hooks/useMe";
import { useThemeSync } from "@/hooks/useTheme";
import { signOut } from "@/lib/auth-client";
import { setUserTimezone } from "@/lib/dates";
import { MOBILE_PRIMARY, type NavItem } from "@/lib/nav";
import { useNav } from "@/hooks/useNav";
import { cn, isMac } from "@/lib/utils";
import { t } from "@shared/i18n";

function NavEntry({ item, onNavigate, compact, unread = 0 }: { item: NavItem; onNavigate?: () => void; compact?: boolean; unread?: number }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/app"}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "press group flex items-center gap-3 rounded-xl px-2.5 text-[14px] transition-colors",
          compact ? "py-1.5" : "py-2",
          isActive ? "bg-elevated/90 font-semibold text-fg shadow-[0_1px_3px_rgb(0_0_0/0.08),inset_0_0_0_1px_var(--glass-border)]" : "font-medium text-muted hover:bg-elevated/50 hover:text-fg",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-[9px] transition-all", isActive ? "app-icon" : "bg-surface text-subtle group-hover:text-muted")}>
            <item.icon className="size-4" />
          </span>
          <span className="flex-1 truncate">{item.label}</span>
          {item.phase && <span className="text-[10px] font-medium uppercase tracking-wide text-subtle">{t("Bientôt")}</span>}
          {item.id === "notifications" && unread > 0 && <span className="rounded-full bg-accent px-1.5 text-[11px] font-semibold tabular-nums text-white">{unread > 99 ? "99+" : unread}</span>}
        </>
      )}
    </NavLink>
  );
}

function UserMenu() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const update = useUpdateProfile({ silent: true });
  const dark = document.documentElement.classList.contains("dark");
  if (!me) return null;
  const initials = me.user.name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <Menu>
      <MenuTrigger asChild>
        <button className="flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-elevated/60" aria-label={t("Menu utilisateur")}>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand text-[12px] font-semibold text-brand-fg">{initials}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium">{me.user.name}</span>
            <span className="block truncate text-[12px] text-muted">{me.user.email}</span>
          </span>
        </button>
      </MenuTrigger>
      <MenuContent align="start" className="w-60">
        <MenuLabel>
          {me.workspace.name} {t("· Formule")} {PLAN_NAMES[me.workspace.plan]}
        </MenuLabel>
        <MenuItem icon={<User />} onSelect={() => navigate("/app/settings")}>
          {t("Profil et paramètres")}
        </MenuItem>
        <MenuItem icon={<CreditCard />} onSelect={() => navigate("/app/settings/subscription")}>
          {t("Abonnement")}
        </MenuItem>
        <MenuItem icon={dark ? <Sun /> : <Moon />} onSelect={() => update.mutate({ theme: dark ? "light" : "dark" })}>
          {dark ? t("Mode clair") : t("Mode sombre")}
        </MenuItem>
        <MenuItem icon={<HelpCircle />} onSelect={() => navigate("/help")}>
          {t("Aide")}
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          danger
          icon={<LogOut />}
          onSelect={async () => {
            await signOut();
            qc.clear();
            navigate("/login", { replace: true });
          }}
        >
          {t("Se déconnecter")}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

export function AppShell() {
  const { data: me } = useMe();
  useThemeSync(me?.profile.theme, me?.profile.density);
  if (me) setUserTimezone(me.profile.timezone);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const unreadQ = useQuery({ queryKey: ["notifications", "unread-count"], queryFn: async () => (await api<{ data: { count: number } }>("/api/notifications/unread-count")).data.count, refetchInterval: 120_000 });
  const unread = unreadQ.data ?? 0;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const NAV = useNav();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Invitation ouverte avant inscription : on y revient une fois le compte prêt.
  useEffect(() => {
    let token: string | null = null;
    try {
      token = localStorage.getItem("gbc.pending-invite");
      localStorage.removeItem("gbc.pending-invite");
    } catch {
      token = null;
    }
    if (token && /^[0-9a-f]{64}$/.test(token)) navigate(`/invite/${token}`);
  }, [navigate]);

  // Raccourcis clavier : ⌘/Ctrl+K (recherche), G puis O/C/S (navigation).
  useEffect(() => {
    let gPressed = 0;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing = target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.toLowerCase() === "g") {
        gPressed = Date.now();
        return;
      }
      if (Date.now() - gPressed < 800) {
        const map: Record<string, string> = { o: "/app", c: "/app/coach", s: "/app/settings", t: "/app/tasks", p: "/app/planning", m: "/app/missions" };
        const to = map[e.key.toLowerCase()];
        if (to) navigate(to);
        gPressed = 0;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center justify-between px-5">
        <Logo />
        <ThemeToggle className="size-9" />
      </div>
      <div className="px-3">
        <WorkspaceSwitcher />
        <button
          onClick={() => setPaletteOpen(true)}
          className="press mb-3 flex h-10 w-full items-center gap-2 rounded-xl border border-border bg-elevated/70 px-3 text-[13px] text-subtle hover:border-border-strong"
        >
          <Search className="size-4" />
          <span className="flex-1 text-left">{t("Rechercher…")}</span>
          <Kbd>{isMac ? "⌘" : "Ctrl"} K</Kbd>
        </button>
      </div>
      <nav aria-label={t("Navigation principale")} className="scrollbar-thin flex-1 space-y-0.5 overflow-y-auto px-3 pb-3">
        {NAV.map((item) => (
          <NavEntry key={item.id} item={item} compact unread={unread} />
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <UserMenu />
      </div>
    </div>
  );

  const primary = NAV.filter((n) => MOBILE_PRIMARY.includes(n.id));

  return (
    <div className="min-h-dvh">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-brand focus:px-3 focus:py-2 focus:text-white">
        {t("Aller au contenu")}
      </a>

      {/* Sidebar persistante (ordinateur) */}
      <aside className="glass-bar fixed inset-y-0 left-0 hidden w-64 border-r border-border/70 lg:block">{sidebar}</aside>

      {/* En-tête mobile */}
      <header className="pt-safe glass-bar sticky top-0 z-30 border-b border-border/60 lg:hidden">
        <div className="flex h-14 items-center justify-between px-4">
        <Logo />
        <div className="flex items-center">
          <ThemeToggle />
          <NavLink to="/app/notifications" className="press relative flex size-10 items-center justify-center rounded-full text-muted hover:bg-surface" aria-label={t("Notifications{0}", unread ? ` (${unread} non lues)` : "")}>
            <Bell className="size-5" />
            {unread > 0 && <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white ring-2 ring-bg">{unread > 9 ? "9+" : unread}</span>}
          </NavLink>
          <button onClick={() => setPaletteOpen(true)} className="press flex size-10 items-center justify-center rounded-full text-muted hover:bg-surface" aria-label={t("Rechercher")}>
            <Search className="size-5" />
          </button>
        </div>
        </div>
      </header>

      <main id="main" className="min-w-0 pb-32 lg:pb-0 lg:pl-64">
        <div key={location.pathname.split("/").slice(0, 3).join("/")} className="animate-page">
          <Outlet />
        </div>
      </main>

      {/* Barre d'onglets flottante (mobile) */}
      <nav aria-label={t("Navigation mobile")} className="pointer-events-none fixed inset-x-0 bottom-0 z-30 px-3 pb-[max(env(safe-area-inset-bottom),12px)] lg:hidden">
        <div className="glass-pop pointer-events-auto mx-auto grid max-w-md grid-cols-5 !rounded-[28px] p-1.5">
          {primary.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              end={item.to === "/app"}
              className={({ isActive }) => cn("press relative flex flex-col items-center gap-0.5 rounded-[22px] py-2 text-[10.5px] font-semibold transition-colors", isActive ? "text-fg" : "text-muted")}
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="animate-pop absolute inset-0 rounded-[22px] bg-brand/20" aria-hidden />}
                  <item.icon className={cn("relative size-[22px] transition-transform", isActive && "scale-110 text-primary")} strokeWidth={isActive ? 2.4 : 2} />
                  <span className="relative max-w-full truncate px-1">{item.label.split(" ")[0]}</span>
                </>
              )}
            </NavLink>
          ))}
          <button onClick={() => setDrawerOpen(true)} className="press flex flex-col items-center gap-0.5 rounded-[22px] py-2 text-[10.5px] font-semibold text-muted" aria-label={t("Plus de sections")}>
            <MenuIcon className="size-[22px]" />
            {t("Plus")}
          </button>
        </div>
      </nav>

      {/* Menu « Plus » : grille d'icônes façon écran d'accueil iOS */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label={t("Toutes les sections")}>
          <div className="anim-overlay absolute inset-0 bg-black/45 backdrop-blur-[3px]" data-state="open" onClick={() => setDrawerOpen(false)} />
          <div className="anim-sheet glass-pop absolute inset-x-0 bottom-0 max-h-[88dvh] overflow-y-auto !rounded-b-none !rounded-t-[30px] px-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-2" data-state="open">
            <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-border-strong" aria-hidden />
            <div className="mb-4 flex items-center justify-between">
              <p className="text-lg font-bold">{t("Toutes les sections")}</p>
              <button onClick={() => setDrawerOpen(false)} className="press flex size-8 items-center justify-center rounded-full bg-surface text-muted" aria-label={t("Fermer le menu")}>
                <X className="size-4" />
              </button>
            </div>
            <div className="stagger grid grid-cols-4 gap-x-2 gap-y-5">
              {NAV.map((item, i) => (
                <NavLink key={item.id} to={item.to} end={item.to === "/app"} className="press flex flex-col items-center gap-1.5 text-center">
                  <span className={cn("relative size-[58px] [&_svg]:size-7", i % 3 === 2 ? "app-icon app-icon-slate" : "app-icon")}>
                    <item.icon />
                    {item.id === "notifications" && unread > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[11px] font-bold text-white ring-2 ring-[var(--glass-strong)]">{unread > 9 ? "9+" : unread}</span>}
                  </span>
                  <span className="line-clamp-2 text-[11px] font-medium leading-tight">{item.label}</span>
                </NavLink>
              ))}
            </div>
            <div className="mt-6 space-y-3 border-t border-border/70 pt-4">
              <WorkspaceSwitcher />
              <UserMenu />
            </div>
          </div>
        </div>
      )}

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <UpgradeDialog />
      <AiConsentDialog />
      <ReminderAlarms />
    </div>
  );
}
